import React from 'react'
import { SentenceUnit, TranslationViewModel } from 'lib/translation-view-model'
import { Translit } from 'lib/transliteration'
import { isThaiText } from 'lib/generic-utils/strings'

type Props = {
   model: TranslationViewModel
   activeIndex: number
   onSelect: (index: number) => void
   translit: Translit
}

/**
 * The Thai text of a card, built from the word objects (for English input the
 * raw line is the user's English, but the word objects are the Thai
 * translation). The thai font class only goes on words that are actually Thai.
 */
const thaiRow = (unit: SentenceUnit): React.ReactNode =>
   unit.words.length ? (
      unit.words.map((word, index) => (
         <span key={index} className={word.isThai ? 'thai text-[17px]!' : undefined}>
            {word.word}
            {word.hasTrailingSpace ? ' ' : ''}
         </span>
      ))
   ) : (
      <span className={isThaiText(unit.text) ? 'thai text-[17px]!' : undefined}>{unit.text}</span>
   )

/**
 * Sticky sentence picker beside the word breakdown. Desktop only — on small
 * screens sentence switching happens via the text itself and the Next button.
 */
const SentenceRail = (props: Props) => {
   const { model, activeIndex } = props

   return (
      <div className="max-md:hidden flex-none w-[300px] sticky top-5">
         <div className="flex flex-col gap-3.5 max-h-[62vh] overflow-y-auto pr-1 [scrollbar-width:thin]">
            {model.paragraphs.map((paragraph, pIndex) => (
               <div key={pIndex}>
                  {model.paragraphs.length > 1 && (
                     <div className="text-[11.5px] font-semibold uppercase tracking-[1.1px] text-paragraphLabel mb-1.5 select-none">
                        Paragraph {pIndex + 1}
                     </div>
                  )}
                  <div className="flex flex-col gap-2">
                     {paragraph.blocks.flatMap((block) =>
                        block.sentences.map((unit) => {
                           const isActive = unit.index === activeIndex
                           return (
                              <button
                                 type="button"
                                 key={unit.index}
                                 onClick={() => props.onSelect(unit.index)}
                                 className={`flex gap-[11px] w-full text-left py-[11px] px-[13px] rounded-[14px] cursor-pointer border border-solid transition-colors hover:border-railBorderActive ${
                                    isActive
                                       ? 'bg-lightBlueTransparent border-railBorderActive'
                                       : 'bg-white border-railBorder'
                                 }`}
                              >
                                 <span
                                    className={`flex-none w-[22px] h-[22px] grid place-items-center rounded-full text-xs font-black ${
                                       isActive
                                          ? 'bg-accentBlue text-white'
                                          : 'bg-backgroundGray text-textMuted'
                                    }`}
                                 >
                                    {block.marker ?? unit.index + 1}
                                 </span>
                                 <span className="min-w-0 block">
                                    <span className="block text-[15px] leading-[1.4]">
                                       {thaiRow(unit)}
                                    </span>
                                    {!unit.failed && props.translit.enabled && (
                                       <span className="block text-[13.5px] italic leading-[1.45] text-translit mt-0.5">
                                          {props.translit.of(unit.line.phonetic)}
                                       </span>
                                    )}
                                    <span className="block text-[13.5px] leading-[1.45] text-bodyGray mt-0.5">
                                       {unit.failed ? (
                                          <i className="opacity-60">
                                             Couldn&rsquo;t translate this line
                                          </i>
                                       ) : (
                                          unit.english
                                       )}
                                    </span>
                                 </span>
                              </button>
                           )
                        })
                     )}
                  </div>
               </div>
            ))}
         </div>
      </div>
   )
}

export default SentenceRail
